import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  maxVisiblePages?: number;
  showFirstLast?: boolean;
  className?: string;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  maxVisiblePages = 3,
  showFirstLast = false,
  className = '',
}) => {
  const getVisiblePages = () => {
    const pages: (number | string)[] = [];
    const halfVisible = Math.floor(maxVisiblePages / 2);

    let startPage = Math.max(1, currentPage - halfVisible);
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Khi showFirstLast = true, chỉ hiển thị các trang trong khoảng maxVisiblePages
    if (showFirstLast) {
      // Chỉ thêm các trang trong khoảng startPage đến endPage
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      return { pages, startPage, endPage };
    }

    // Khi showFirstLast = false, thêm trang đầu và cuối nếu cần
    if (startPage > 1) {
      pages.push(1);
      if (startPage > 2) {
        pages.push('...');
      }
    }

    // Thêm các trang hiển thị
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    // Thêm trang cuối nếu cần
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push('...');
      }
      pages.push(totalPages);
    }

    return { pages, startPage, endPage };
  };

  const handlePageClick = (page: number | string) => {
    if (typeof page === 'number' && page !== currentPage) {
      onPageChange(page);
    }
  };

  const { pages: visiblePages, startPage, endPage } = getVisiblePages();

  // Kiểm tra xem có cần hiển thị nút First/Last không
  const shouldShowFirstButton = showFirstLast && currentPage > 2 && startPage > 1;
  const shouldShowLastButton =
    showFirstLast && currentPage < totalPages - 1 && endPage < totalPages;

  return (
    <div data-component-id="src/components/ui/Pagination.tsx:79:4" data-component-path="src/components/ui/Pagination.tsx" data-component-line="79" data-component-end-line="157" data-component-file="Pagination.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
      className={`flex whitespace-nowrap justify-center items-center gap-2 px-2 min-w-fit w-full ${className}`}
    >
      {/* Nút Previous */}
      <button data-component-id="src/components/ui/Pagination.tsx:83:6" data-component-path="src/components/ui/Pagination.tsx" data-component-line="83" data-component-end-line="90" data-component-file="Pagination.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22w-10%20h-10%20flex-shrink-0%20flex%20items-center%20justify-center%20rounded%20border%20text-%5B%234a3b63%5D%20hover%3Abg-gray-100%20disabled%3Aopacity-50%20disabled%3Acursor-not-allowed%22%7D"
        className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded border text-[#4a3b63] hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
        disabled={currentPage <= 1}
        onClick={() => handlePageClick(currentPage - 1)}
        aria-label="Trang trước"
      >
        <ChevronLeft data-component-id="src/components/ui/Pagination.tsx:89:8" data-component-path="src/components/ui/Pagination.tsx" data-component-line="89" data-component-end-line="89" data-component-file="Pagination.tsx" data-component-name="ChevronLeft" data-component-content="%7B%22elementName%22%3A%22ChevronLeft%22%7D" size={18} />
      </button>

      {/* Nút First (tùy chọn) */}
      {shouldShowFirstButton && (
        <button data-component-id="src/components/ui/Pagination.tsx:94:8" data-component-path="src/components/ui/Pagination.tsx" data-component-line="94" data-component-end-line="100" data-component-file="Pagination.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22w-10%20h-10%20flex-shrink-0%20flex%20items-center%20justify-center%20rounded%20border%20transition-all%20bg-white%20text-%5B%234a3b63%5D%20hover%3Abg-gray-100%20border-gray-300%22%2C%22textContent%22%3A%221%22%7D"
          className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded border transition-all bg-white text-[#4a3b63] hover:bg-gray-100 border-gray-300"
          onClick={() => handlePageClick(1)}
          aria-label="Trang đầu"
        >
          1
        </button>
      )}

      {/* Các trang */}
      {visiblePages.map((page, index) => {
        if (page === '...') {
          return (
            <button data-component-id="src/components/ui/Pagination.tsx:107:12" data-component-path="src/components/ui/Pagination.tsx" data-component-line="107" data-component-end-line="113" data-component-file="Pagination.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22w-10%20h-10%20flex-shrink-0%20flex%20items-center%20justify-center%20rounded%20border%20transition-all%20bg-white%20text-%5B%234a3b63%5D%20border-gray-300%20cursor-not-allowed%22%2C%22textContent%22%3A%22...%22%7D"
              key={`ellipsis-${index}`}
              disabled
              className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded border transition-all bg-white text-[#4a3b63] border-gray-300 cursor-not-allowed"
            >
              ...
            </button>
          );
        }

        const pageNumber = page as number;
        const isActive = pageNumber === currentPage;

        return (
          <button data-component-id="src/components/ui/Pagination.tsx:121:10" data-component-path="src/components/ui/Pagination.tsx" data-component-line="121" data-component-end-line="133" data-component-file="Pagination.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
            key={pageNumber}
            className={`w-10 h-10 flex-shrink-0 flex items-center justify-center rounded border transition-all ${
              isActive
                ? 'bg-[#4a3b63] text-white border-[#4a3b63]'
                : 'bg-white text-[#4a3b63] hover:bg-gray-100 border-gray-300'
            }`}
            onClick={() => handlePageClick(pageNumber)}
            aria-label={`Trang ${pageNumber}`}
            aria-current={isActive ? 'page' : undefined}
          >
            {pageNumber}
          </button>
        );
      })}

      {/* Nút Last (tùy chọn) */}
      {shouldShowLastButton && (
        <button data-component-id="src/components/ui/Pagination.tsx:139:8" data-component-path="src/components/ui/Pagination.tsx" data-component-line="139" data-component-end-line="145" data-component-file="Pagination.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22w-10%20h-10%20flex-shrink-0%20flex%20items-center%20justify-center%20rounded%20border%20transition-all%20bg-white%20text-%5B%234a3b63%5D%20hover%3Abg-gray-100%20border-gray-300%22%7D"
          className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded border transition-all bg-white text-[#4a3b63] hover:bg-gray-100 border-gray-300"
          onClick={() => handlePageClick(totalPages)}
          aria-label="Trang cuối"
        >
          {totalPages}
        </button>
      )}

      {/* Nút Next */}
      <button data-component-id="src/components/ui/Pagination.tsx:149:6" data-component-path="src/components/ui/Pagination.tsx" data-component-line="149" data-component-end-line="156" data-component-file="Pagination.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22w-10%20h-10%20flex-shrink-0%20flex%20items-center%20justify-center%20rounded%20border%20text-%5B%234a3b63%5D%20hover%3Abg-gray-100%20disabled%3Aopacity-50%20disabled%3Acursor-not-allowed%22%7D"
        className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded border text-[#4a3b63] hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
        disabled={currentPage >= totalPages}
        onClick={() => handlePageClick(currentPage + 1)}
        aria-label="Trang tiếp"
      >
        <ChevronRight data-component-id="src/components/ui/Pagination.tsx:155:8" data-component-path="src/components/ui/Pagination.tsx" data-component-line="155" data-component-end-line="155" data-component-file="Pagination.tsx" data-component-name="ChevronRight" data-component-content="%7B%22elementName%22%3A%22ChevronRight%22%7D" size={18} />
      </button>
    </div>
  );
};

export default Pagination;
